/**
 * Totales de cobro POS: mercadería + propina + medios de pago.
 * Una sola fuente para UI, validación y tickets (evita vuelto espurio con propina).
 */
import type { CartEvaluationResult } from '../../promotions/domain/promotionTypes';
import type { PosDraftPayment } from '../domain/posTypes';
import type { CartItem } from '../store/usePosStore';
import { derivePosCheckoutTotals } from './posScreen.utils';
import { applyTipToCheckoutTotals, roundTipMoney } from './posCheckoutTip';

const round2 = (n: number) => Math.round(n * 100) / 100;

export type PosCheckoutMoneySnapshot = ReturnType<typeof derivePosCheckoutTotals> & {
  merchandiseTotal: number;
  tipAmount: number;
};

/** Vuelto solo con efectivo en el cobro; el total a pagar incluye propina. */
export function checkoutChangeAmount(
  totalIngresado: number,
  totalDue: number,
  paymentDrafts: Pick<PosDraftPayment, 'methodId'>[],
): number {
  const diff = round2(totalIngresado - totalDue);
  if (diff <= 0.005) return 0;
  if (!paymentDrafts.some((d) => d.methodId === 'cash')) return 0;
  return diff;
}

export function derivePosCheckoutMoney(
  cart: CartItem[],
  paymentDrafts: PosDraftPayment[],
  cartEvaluation?: CartEvaluationResult | null,
  options?: { extraDiscountTotal?: number; tipAmount?: number },
): PosCheckoutMoneySnapshot {
  const tipAmount = roundTipMoney(options?.tipAmount ?? 0);
  const merchandise = derivePosCheckoutTotals(
    cart,
    paymentDrafts,
    cartEvaluation,
    options?.extraDiscountTotal ?? 0,
  );
  const withTip = applyTipToCheckoutTotals(merchandise, tipAmount);
  const vuelto = checkoutChangeAmount(withTip.totalIngresado, withTip.total, paymentDrafts);
  const saldoPendiente = round2(Math.max(withTip.total - withTip.totalIngresado, 0));
  return { ...withTip, vuelto, saldoPendiente };
}

/** Vuelto en detalle de venta (post-cobro). */
export function saleDetailChangeAmount(detail: {
  total: number;
  payments: { methodId: string; amount: number; tenderedAmount?: number | null }[];
}): number | null {
  const hasCash = detail.payments.some((p) => p.methodId === 'cash');
  if (!hasCash) return null;
  let fromTendered = 0;
  for (const p of detail.payments) {
    if (p.methodId !== 'cash') continue;
    const tendered = p.tenderedAmount;
    if (tendered != null && tendered > p.amount + 0.005) {
      fromTendered += tendered - p.amount;
    }
  }
  if (fromTendered > 0.005) return round2(fromTendered);
  const ingresado = detail.payments.reduce((s, p) => s + p.amount, 0);
  const diff = round2(ingresado - detail.total);
  return diff > 0.005 ? diff : null;
}
